/**
 * PartsPickupPanel (PARTS-PICKUP-01, Seth 2026-09-02) — the office's per-tech
 * pickup list for a day: what each tech grabs before rolling (filters and
 * pads the notes call for, ordered parts that arrived for today's return
 * visits, vendor will-call stops) PLUS whatever the office adds by hand.
 * Lives on Today → Parts Desk (mode "today": prep tomorrow, check this
 * morning) and on Reports → Parts for the Day (mode "report": the printable
 * sheet). The tech's phone shows the same list and ticks items off.
 *
 * Competitor frame: ServiceTitan's truck replenishment / FieldEdge's pick
 * lists both tie parts to the job; neither lets the office hand-edit the
 * morning list per tech. This does.
 */
import { useMemo, useState, type FormEvent } from "react";
import { Truck, Plus, X, Check, Printer, RotateCcw, Package, Info, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { cn } from "@/lib/cn";
import { Card } from "@/components/ui/Card";
import { CustomerLink } from "@/components/links";
import { TechAvatar } from "@/components/TechAvatar";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { PART_CHIP, type PartKind } from "./truckParts";
import {
  usePickupDay, useAddPickupItem, useRemovePickupItem, useRestorePickupItem, useStampPickupItem, useSetPickupFrom,
  visibleItems, grabList, type PickupItem, type PickupTech,
} from "./pickupList";

const isoOf = (d: Date) => d.toLocaleDateString("en-CA");
const addDays = (iso: string, n: number) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + n); return isoOf(d); };
const fmtDay = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

const SOURCE_META: Record<PickupItem["source"], { label: string; chip: string }> = {
  auto: { label: "From the Notes", chip: "bg-slate-100 text-slate-600" },
  ticket: { label: "Arrived Part", chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  pickup: { label: "Part Pickup", chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  office: { label: "Added by Office", chip: "bg-brand-50 text-brand-700 ring-1 ring-brand-100" },
};

const KIND_OPTIONS: { value: PartKind; label: string }[] = [
  { value: "part", label: "Part" },
  { value: "filter", label: "Filter" },
  { value: "aprilaire", label: "Aprilaire" },
  { value: "humidifier", label: "Humidifier Pad" },
  { value: "cabinet", label: "Cabinet" },
  { value: "gear", label: "Gear" },
];

export function PartsPickupPanel({ mode = "today" }: { mode?: "today" | "report" }) {
  const today = isoOf(new Date());
  const [day, setDay] = useState(today);
  const { data, isLoading, isError } = usePickupDay(day);
  const canEdit = useCan("board.manage");
  const techs = data ?? [];
  const withItems = techs.filter((t) => visibleItems(t).length > 0);
  const totals = useMemo(() => {
    let items = 0, office = 0, prepped = 0, picked = 0;
    for (const t of techs) for (const i of visibleItems(t)) {
      items += 1;
      if (i.source === "office") office += 1;
      if (i.preppedAt) prepped += 1;
      if (i.pickedUpAt) picked += 1;
    }
    return { items, office, prepped, picked };
  }, [techs]);
  const [showQuiet, setShowQuiet] = useState(false);

  return (
    <div className={cn("min-w-0", mode === "report" && "flex-1")}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Truck className="h-4 w-4" /></span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-900">Parts Pickup · {fmtDay(day)}</div>
          <div className="text-[11.5px] text-slate-500">
            {isLoading ? "Building the list…" : `${withItems.length} tech${withItems.length === 1 ? "" : "s"} · ${totals.items} item${totals.items === 1 ? "" : "s"}${totals.office ? ` · ${totals.office} added by the office` : ""}${totals.prepped ? ` · ${totals.prepped} prepped` : ""}${totals.picked ? ` · ${totals.picked} picked up` : ""}`}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5 print:hidden">
          <DayChip label="Today" active={day === today} onClick={() => setDay(today)} />
          <DayChip label="Tomorrow" active={day === addDays(today, 1)} onClick={() => setDay(addDays(today, 1))} />
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)}
            className="h-8 rounded-full border border-hairline bg-white px-3 text-xs font-medium text-slate-700 focus:border-brand-400 focus:outline-none" />
          {/* PARTS-PRINT-01: printing opens the chrome-free pick sheet in its own
              tab — the page itself printed the sidebar (Seth 2026-09-23). */}
          <button type="button" onClick={() => window.open(`/parts/print?day=${day}`, "_blank", "noopener")}
            title="The pick sheet for the day — one block per tech, a box to tick for every item"
            className="inline-flex h-8 items-center gap-1 rounded-full border border-hairline bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <Printer className="h-3.5 w-3.5" /> Print sheet
          </button>
        </div>
      </div>

      {mode === "report" && (
        <p className="mb-3 flex items-start gap-1.5 rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2 text-[11px] text-slate-500 print:hidden">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          Filters, pads and cabinets are read from each call's notes; arrived parts come from the parts desk; pickup stops from the board. Where a part gets picked up (office shelf or vendor will-call) comes from the purchase order behind it — click the location chip to change it. Add, remove or prep items here or on Today → Parts Desk — the tech's phone shows the same list and ticks it off.
        </p>
      )}

      {isError ? (
        <Card className="p-6 text-center text-sm text-red-700">Couldn't build the pickup list. Try again in a moment.</Card>
      ) : isLoading ? (
        <Card className="h-32 animate-pulse bg-slate-100" />
      ) : techs.length === 0 ? (
        <Card className="p-8 text-center">
          <Package className="mx-auto h-8 w-8 text-slate-300" />
          <h3 className="mt-2 text-sm font-bold text-slate-900">Nothing scheduled on {fmtDay(day)}</h3>
          <p className="mt-1 text-xs text-slate-500">Pick another day, or add items once the board is built.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {withItems.map((t) => (
            <TechCard key={t.empno} tech={t} day={day} canEdit={canEdit} />
          ))}
          {withItems.length === 0 && (
            <Card className="p-6 text-center text-sm text-slate-500">No parts called out for anyone on {fmtDay(day)} — add an item below if a truck needs something.</Card>
          )}
          {techs.length > withItems.length && (
            <div>
              <button type="button" onClick={() => setShowQuiet((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 print:hidden">
                {showQuiet ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {techs.length - withItems.length} tech{techs.length - withItems.length === 1 ? "" : "s"} with nothing to pick up
              </button>
              {showQuiet && (
                <div className="mt-2 space-y-3">
                  {techs.filter((t) => visibleItems(t).length === 0).map((t) => (
                    <TechCard key={t.empno} tech={t} day={day} canEdit={canEdit} quiet />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** PARTS-PICKUP-02: where the tech picks it up — read from the PO, or set by the office.
 *  The choices render INLINE (the card clips anything absolutely positioned). */
function WhereChip({ item, canEdit, busy, onSet }: { item: PickupItem; canEdit: boolean; busy: boolean; onSet: (from: "office" | "vendor" | null) => void }) {
  const [open, setOpen] = useState(false);
  const tone = item.where === "office" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : item.where === "vendor" ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
    : "bg-slate-100 text-slate-500 ring-1 ring-slate-200";
  const text = item.whereLabel ?? "Where? — set it";
  const hint = item.whereFrom === "po" ? "Read from the purchase order" : item.whereFrom === "override" ? "Set by the office" : "No purchase order found for this stop";
  const pick = (from: "office" | "vendor" | null) => { onSet(from); setOpen(false); };
  return (
    <>
      <button type="button" disabled={!canEdit || busy} onClick={() => setOpen((v) => !v)} title={`${hint}${canEdit ? " — click to change" : ""}`}
        className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-px text-[10.5px] font-semibold print:ring-0", tone, canEdit && "hover:brightness-95")}>
        <MapPin className="h-3 w-3" /> {text}
      </button>
      {open && (
        <span className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-hairline bg-white px-1.5 py-1 text-[10.5px] print:hidden">
          <span className="text-slate-400">Picked up:</span>
          <button type="button" onClick={() => pick("office")} className="rounded-full bg-emerald-50 px-2 py-px font-semibold text-emerald-700 ring-1 ring-emerald-200 hover:brightness-95">At the office</button>
          <button type="button" onClick={() => pick("vendor")} className="rounded-full bg-amber-50 px-2 py-px font-semibold text-amber-800 ring-1 ring-amber-200 hover:brightness-95">Vendor will-call</button>
          {item.pickupFrom && (
            <button type="button" onClick={() => pick(null)} className="px-1 font-medium text-slate-500 hover:text-slate-800">Let the PO decide</button>
          )}
          <button type="button" onClick={() => setOpen(false)} className="rounded p-0.5 text-slate-300 hover:text-slate-600" aria-label="Close"><X className="h-3 w-3" /></button>
        </span>
      )}
    </>
  );
}

function DayChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("h-8 rounded-full px-3 text-xs font-semibold transition-colors", active ? "bg-brand-700 text-white" : "border border-hairline bg-white text-slate-700 hover:bg-slate-50")}>
      {label}
    </button>
  );
}

function TechCard({ tech, day, canEdit, quiet }: { tech: PickupTech; day: string; canEdit: boolean; quiet?: boolean }) {
  const items = visibleItems(tech);
  const hidden = tech.items.filter((i) => i.hidden);
  const grab = grabList(tech);
  const prepped = items.filter((i) => i.preppedAt).length;
  const [adding, setAdding] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const remove = useRemovePickupItem();
  const restore = useRestorePickupItem();
  const stamp = useStampPickupItem();
  const setFrom = useSetPickupFrom();
  const first = tech.name.split(" ")[0] || tech.name;

  return (
    <Card className="overflow-hidden print:break-inside-avoid">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
        <TechAvatar empno={tech.empno} name={tech.name} size="sm" />
        <span className="text-sm font-bold text-slate-900">{tech.name}</span>
        <span className="text-[11px] text-slate-400">{tech.jobs.length} stop{tech.jobs.length === 1 ? "" : "s"}{items.length ? ` · ${items.length} item${items.length === 1 ? "" : "s"}` : ""}</span>
        {items.length > 0 && (
          <span className={cn("ml-auto rounded-full px-2 py-0.5 text-[10.5px] font-bold", prepped === items.length ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500")}>
            {prepped === items.length ? "All prepped" : `${prepped}/${items.length} prepped`}
          </span>
        )}
      </div>

      {grab.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 bg-amber-50/30 px-4 py-2">
          <span className="text-[12px] font-semibold text-amber-700">Grab list</span>
          {grab.map((g) => (
            <span key={g.label} className={cn("rounded-md border px-2 py-0.5 text-[11px] font-medium", PART_CHIP[g.kind])}>
              <strong className="tabular-nums">{g.n}×</strong> {g.label}
            </span>
          ))}
        </div>
      )}

      <ul className="divide-y divide-slate-100">
        {items.map((i) => (
          <li key={i.key} className="flex items-start gap-3 px-4 py-2">
            <label className="mt-0.5 flex shrink-0 items-center print:hidden" title={i.preppedAt ? `Prepped ${fmtTime(i.preppedAt)}` : "Mark prepped (ready at the shop)"}>
              <input type="checkbox" checked={!!i.preppedAt} disabled={!canEdit || stamp.isPending}
                onChange={(e) => stamp.mutate({ day, empno: tech.empno, item: i, field: "prepped", on: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-400" />
            </label>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className={cn("rounded-md border px-2 py-0.5 text-xs font-semibold", PART_CHIP[i.kind])}>{i.qty > 1 ? `${i.qty}× ` : ""}{i.label}</span>
                <span className={cn("rounded-full px-1.5 py-px text-[10.5px] font-semibold", SOURCE_META[i.source].chip)}>{SOURCE_META[i.source].label}</span>
                {i.note && <span className="text-[11.5px] text-slate-500">{i.note}</span>}
                {(i.source === "pickup" || i.source === "office") && (
                  <WhereChip item={i} canEdit={canEdit} busy={setFrom.isPending}
                    onSet={(from) => setFrom.mutate({ day, empno: tech.empno, item: i, from })} />
                )}
                {i.pickedUpAt && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-px text-[10.5px] font-bold text-emerald-700"><Check className="h-3 w-3" /> Picked up {fmtTime(i.pickedUpAt)}</span>
                )}
              </div>
              {i.job ? (
                <div className="mt-0.5 text-[11.5px] text-slate-500">
                  for <CustomerLink custno={i.job.custno} locno={i.job.locno ?? undefined} className="font-medium text-slate-700">{i.job.name}</CustomerLink>
                  {[i.job.address, i.job.city].filter(Boolean).length ? ` · ${[i.job.address, i.job.city].filter(Boolean).join(", ")}` : ""}
                  {i.job.window ? ` · ${i.job.window}` : ""}
                  {i.job.tuneup ? ` · ${i.job.tuneup}` : ""}
                </div>
              ) : (
                <div className="mt-0.5 text-[11.5px] text-slate-400">for {first}'s truck</div>
              )}
            </div>
            {canEdit && (
              <button type="button" disabled={remove.isPending} onClick={() => remove.mutate({ day, empno: tech.empno, item: i })}
                title={i.source === "office" ? "Delete this item" : "Remove from the list (the note stays as it is)"}
                className="shrink-0 rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-rose-600 print:hidden">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && (
          <li className="px-4 py-3 text-xs text-slate-400">{quiet ? "Nothing called out in the notes." : "Nothing left on the list."}</li>
        )}
      </ul>

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 py-2 print:hidden">
        {canEdit && !adding && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline">
            <Plus className="h-3.5 w-3.5" /> Add an item for {first}
          </button>
        )}
        {hidden.length > 0 && (
          <button type="button" onClick={() => setShowHidden((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-700">
            {hidden.length} removed
          </button>
        )}
      </div>
      {adding && <AddItemForm tech={tech} day={day} onDone={() => setAdding(false)} />}
      {showHidden && hidden.length > 0 && (
        <ul className="border-t border-dashed border-slate-200 bg-slate-50/50 px-4 py-2 print:hidden">
          {hidden.map((i) => (
            <li key={i.key} className="flex items-center gap-2 py-1 text-xs text-slate-400">
              <span className="line-through">{i.qty > 1 ? `${i.qty}× ` : ""}{i.label}</span>
              {i.job && <span>· {i.job.name}</span>}
              {canEdit && i.rowId && (
                <button type="button" disabled={restore.isPending} onClick={() => restore.mutate({ day, rowId: i.rowId! })} className="ml-auto inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline">
                  <RotateCcw className="h-3 w-3" /> Put back
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AddItemForm({ tech, day, onDone }: { tech: PickupTech; day: string; onDone: () => void }) {
  const add = useAddPickupItem();
  const [label, setLabel] = useState("");
  const [qty, setQty] = useState(1);
  const [kind, setKind] = useState<PartKind>("part");
  const [jobRef, setJobRef] = useState<string>("");
  const [note, setNote] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    const job = tech.jobs.find((j) => j.ref === jobRef) ?? null;
    await add.mutateAsync({ day, empno: tech.empno, label, qty, kind, note, dispatchRef: job?.ref ?? null, custno: job?.custno ?? null });
    setLabel(""); setQty(1); setNote("");
    onDone();
  };
  return (
    <form onSubmit={submit} className="grid gap-2 border-t border-slate-100 bg-brand-50/30 px-4 py-3 sm:grid-cols-[1fr_72px_140px_1fr_auto] print:hidden">
      <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What to grab — e.g. 16x25x4 filter, contactor 40A, APR35 pad"
        className="h-8 rounded-md border border-hairline bg-white px-2.5 text-sm focus:border-brand-500 focus:outline-none" />
      <input type="number" min={1} value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} aria-label="Quantity"
        className="h-8 rounded-md border border-hairline bg-white px-2 text-sm tabular-nums focus:border-brand-500 focus:outline-none" />
      <select value={kind} onChange={(e) => setKind(e.target.value as PartKind)} aria-label="Kind"
        className="h-8 rounded-md border border-hairline bg-white px-2 text-sm focus:border-brand-500 focus:outline-none">
        {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
      </select>
      <select value={jobRef} onChange={(e) => setJobRef(e.target.value)} aria-label="For which call"
        className="h-8 rounded-md border border-hairline bg-white px-2 text-sm focus:border-brand-500 focus:outline-none">
        <option value="">For the truck (no specific call)</option>
        {tech.jobs.filter((j) => !j.isPickupStop).map((j) => (
          <option key={j.ref} value={j.ref}>{j.name}{j.city ? ` · ${j.city}` : ""}</option>
        ))}
      </select>
      <div className="flex items-center gap-1.5">
        <button type="submit" disabled={add.isPending || !label.trim()} className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-700 px-3 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-40">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
        <button type="button" onClick={onDone} className="h-8 px-2 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the tech (optional) — e.g. on the will-call shelf, ask for Dave"
        className="h-8 rounded-md border border-hairline bg-white px-2.5 text-xs focus:border-brand-500 focus:outline-none sm:col-span-5" />
      {add.isError && <p className="text-xs text-red-700 sm:col-span-5">{(add.error as Error)?.message}</p>}
    </form>
  );
}
